'use client';

import React from 'react';
import Link from 'next/link';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { useGetProviderStrategyTabsQuery } from 'src/api/redux';
import {
  useGetStrategyMetricsQuery,
  useGetTargetStrategyProviderDetailsQuery,
} from 'src/api/redux/strategy';
import { useGetTargetStrategyDetailsQuery } from 'src/api/redux/strategy/targetStrategyApi';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faBullseyeArrow } from '@fortawesome/pro-solid-svg-icons';
import { EngageStrategySection } from './components';
import { StrategyFreshFacesBadge } from 'src/components/Strategy/shared';
import { StrategyProfileMetricItem } from 'src/components/Strategy/Profiles/components';
import {
  getMetricValue,
  MetricLocation,
} from 'src/components/Strategy/Profiles/utils';
import { getProfileUrl } from 'src/utils/profileUrl';
import { CircularProgress } from 'src/components/PulseUI/CircularProgress';

interface StrategyProfileContentProps {
  showOverviewHeader?: boolean;
  chainId?: string | number;
  // The provider open in the profile drawer
  providerId: string | number;
  providerType?: string | null;
}

const StrategyProfileContent = ({
  showOverviewHeader = true,
  chainId,
  providerId,
  providerType,
}: StrategyProfileContentProps) => {
  const { selectedStrategy, selectedProvider } = useStrategies(
    LOCATION.STRATEGY_CENTER
  );

  const providerIdStr = providerId?.toString() ?? '';

  // The Strategy Center selection persists across pages, so outside Strategy
  // Center (e.g. Discovery) it can belong to a different provider. Only use it
  // when it is the provider open in the drawer.
  const selectedRow =
    selectedProvider?.provider_id?.toString() === providerIdStr
      ? selectedProvider
      : null;

  // Fetch strategy tabs for the provider to get engage strategies and strategy info
  const { data: strategyTabsResponse } = useGetProviderStrategyTabsQuery(
    { providerId: providerIdStr },
    {
      skip: !providerIdStr,
    }
  );

  // Find the chain we want to display - either from chainId prop or selectedStrategy
  const targetChain = React.useMemo(() => {
    if (!strategyTabsResponse?.results) return null;

    // If chainId is provided, use that to find the chain
    if (chainId) {
      return strategyTabsResponse.results.find(
        (chain: any) => chain.id === chainId || chain.id === Number(chainId)
      );
    }

    // Otherwise, use the selectedStrategy to find the matching chain
    if (selectedStrategy?.id) {
      return strategyTabsResponse.results.find(
        (chain: any) => chain.id === selectedStrategy?.id
      );
    }

    return null;
  }, [chainId, selectedStrategy?.id, strategyTabsResponse]);

  // Use the target strategy from the chain
  // Since selectedStrategy is now a chain object, we must access target_strategy property
  const targetStrategy =
    targetChain?.target_strategy || selectedStrategy?.target_strategy;

  // Fetch full target strategy details to check for code_set and strategy type
  const { data: targetStrategyDetails } = useGetTargetStrategyDetailsQuery(
    targetStrategy?.id?.toString() || '',
    { skip: !targetStrategy?.id }
  );

  // Fetch provider details with metrics for the specific target strategy
  // This is similar to how engage strategies fetch their data
  // currentData, not data: data keeps the previous provider's metrics while
  // the next provider's request is in flight
  const {
    currentData: targetProviderDetails,
    isFetching: isTargetProviderFetching,
  } = useGetTargetStrategyProviderDetailsQuery(
    {
      strategyId: targetStrategy?.id?.toString() || '',
      providerId: providerIdStr,
    },
    {
      skip: !targetStrategy?.id || !providerIdStr,
    }
  );

  // Use provider from API response if available (for specific strategy metrics),
  // otherwise fall back to the Strategy Center row (for global selected strategy)
  const displayProvider = targetProviderDetails || selectedRow;

  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    {
      strategyType: 'target',
      strategyId: targetStrategy?.id?.toString(),
    },
    {
      skip: !targetStrategy?.id,
    }
  );

  // Check if this is a FRESH_FACES strategy
  const isFreshFaces = targetStrategyDetails?.type === 'FRESH_FACES';

  // Fresh Faces can track treating or referring volume — the metric tooltips
  // say "billed" vs "referred" accordingly (PTRI-211)
  const isReferring =
    targetStrategyDetails?.volume_type?.toLowerCase() === 'referring';

  // Extract all engage strategies from the target chain
  const allEngageStrategies = React.useMemo(() => {
    if (!targetChain?.engage_strategies) return [];

    // Return all engage strategies (future: support multiple strategy types)
    return targetChain.engage_strategies;
  }, [targetChain]);

  if (!displayProvider && isTargetProviderFetching) {
    return (
      <div
        className="self-stretch flex justify-center items-center p-4"
        role="status"
        aria-label="Loading strategy metrics"
        data-testid="strategy-profile-content-loading"
      >
        <CircularProgress size="sm" color="neutral" />
      </div>
    );
  }

  if (!displayProvider) {
    return (
      <div className="self-stretch p-4 bg-background-body rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-center items-center gap-4">
        <div className="text-text-tertiary text-sm">No provider selected</div>
      </div>
    );
  }

  return (
    <>
      <div className="self-stretch bg-background-body rounded-lg inline-flex flex-col justify-start items-start gap-4 overflow-visible">
        {showOverviewHeader && (
          <div className="px-2 py-1 bg-sky-50 rounded-2xl inline-flex justify-center items-center gap-0.5 overflow-hidden">
            <div className="w-3 h-3 flex items-center justify-center">
              <Icon icon={faBullseyeArrow} className="text-sky-800" size="xs" />
            </div>
            <div className="text-center justify-start text-sky-800 text-xs font-normal font-['Inter'] leading-none">
              {targetChain?.label || targetStrategy?.label}
            </div>
          </div>
        )}

        {/* Header when not showing overview header (drawer view) */}
        {!showOverviewHeader && targetStrategy && (
          <div className="flex flex-col gap-2 items-start">
            <div className="flex items-center gap-2">
              <p className="text-primary text-lg font-bold">
                {targetStrategy.label}
              </p>
              {isFreshFaces && <StrategyFreshFacesBadge />}
            </div>
            {targetStrategy.description && (
              <p className="text-secondary text-sm">
                {targetStrategy.description}
              </p>
            )}
          </div>
        )}

        {/* Dynamic cells based on strategy metrics */}
        {strategyMetrics?.results?.length > 0 && (
          <div className="self-stretch flex flex-col gap-4 overflow-visible">
            {strategyMetrics.results.map((header, index) => (
              <StrategyProfileMetricItem
                key={header.id}
                header={header}
                index={index}
                selectedProvider={displayProvider as any}
                strategyLabel={targetStrategyDetails?.code_set?.label}
                isFreshFaces={isFreshFaces}
                isReferring={isReferring}
                getMetricValue={getMetricValue}
                renderLocation={(location: MetricLocation) => (
                  <Link
                    href={getProfileUrl(location.type, location.provider_id)}
                    className="text-sky-900 hover:text-sky-600 text-base font-bold underline"
                  >
                    {location.name}
                  </Link>
                )}
              />
            ))}
          </div>
        )}
        {/* Fallback if no strategy metrics */}
        {!strategyMetrics?.results?.length && (
          <div className="self-stretch inline-flex justify-center items-center gap-4">
            <div className="flex-1 self-stretch rounded-lg inline-flex flex-col justify-start items-start gap-2">
              <div className="self-stretch justify-start text-text-tertiary text-sm font-normal font-['Inter'] leading-tight">
                No metrics available
              </div>
              <div className="justify-start text-text-primary text-3xl font-extrabold font-['Inter'] leading-9">
                -
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Engage Strategies - Multiple Types Supported */}
      {/* Keyed by provider too, so switching providers in the drawer remounts
          the section instead of showing the previous provider's members */}
      {allEngageStrategies.map((engageStrategy: any) => (
        <EngageStrategySection
          key={`${providerIdStr}-${engageStrategy.id}`}
          engageStrategy={engageStrategy}
          providerId={providerIdStr}
          providerType={providerType}
          strategyType={engageStrategy.type}
          chainId={chainId || targetChain?.id}
        />
      ))}
    </>
  );
};

export default StrategyProfileContent;
